/**
 * Métricas do Centro Operacional baseadas no chão de fábrica (execuções ativas).
 * SLA estourado: fila > 24h, prazo vencido ou flag is_overdue.
 */

import type { OpsActiveWorkflow, OpsBottleneck } from '../services/operationsCenterService';

/** Fila acima de 24h conta como SLA estourado (regra industrial). */
export const QUEUE_SLA_BREACH_MINUTES = 24 * 60;

export function isWorkflowSlaBreached(w: OpsActiveWorkflow): boolean {
  if (w.is_overdue) return true;
  if (w.sla_remaining_minutes != null && w.sla_remaining_minutes <= 0) return true;
  if ((w.waiting_minutes ?? 0) > QUEUE_SLA_BREACH_MINUTES) return true;
  return false;
}

export function workflowPriorityScore(
  w: OpsActiveWorkflow,
  bottleneckStages: Set<string>,
): number {
  const slaScore = w.is_overdue
    ? 100
    : w.sla_remaining_minutes != null
      ? Math.max(0, Math.min(100, (120 - w.sla_remaining_minutes) / 120 * 100))
      : 0;
  const queueScore = Math.min(100, ((w.waiting_minutes ?? 0) / 60) * 100);
  const bottleneckScore =
    w.current_stage_name && bottleneckStages.has(w.current_stage_name) ? 100 : 0;
  return Math.min(
    100,
    slaScore * 0.4 + queueScore * 0.3 + bottleneckScore * 0.2 + (w.is_overdue ? 10 : 0),
  );
}

/** Prioridade ou rótulo operacional indicando risco explícito. */
export function hasRiskTag(w: OpsActiveWorkflow): boolean {
  const p = (w.priority ?? '').toUpperCase();
  return p.includes('RISCO') || p === 'CRITICAL' || p === 'CRÍTICO' || p === 'HIGH';
}

export function isWorkflowAtRisk(
  w: OpsActiveWorkflow,
  bottleneckStages: Set<string>,
): boolean {
  if (isWorkflowSlaBreached(w)) return true;
  if (hasRiskTag(w)) return true;
  return workflowPriorityScore(w, bottleneckStages) >= 50;
}

export interface OpsFloorKpis {
  slaCompliance: number | null;
  efficiencyRate: number | null;
  slaBreachedCount: number;
  atRiskCount: number;
  totalExecutions: number;
}

export function computeFloorKpis(
  workflows: OpsActiveWorkflow[],
  bottlenecks: OpsBottleneck[],
): OpsFloorKpis {
  const total = workflows.length;
  if (total === 0) {
    return {
      slaCompliance: null,
      efficiencyRate: null,
      slaBreachedCount: 0,
      atRiskCount: 0,
      totalExecutions: 0,
    };
  }

  const bnStages = new Set(bottlenecks.map(b => b.stage_name));
  let breached = 0;
  let atRisk = 0;
  let healthSum = 0;

  for (const w of workflows) {
    const breachedW = isWorkflowSlaBreached(w);
    const score = workflowPriorityScore(w, bnStages);
    const queueHrs = (w.waiting_minutes ?? 0) / 60;

    if (breachedW) breached++;
    if (breachedW || score >= 50) atRisk++;

    const queueMin = w.waiting_minutes ?? 0;
    const severeQueue = queueMin > 100 * 60; // centenas de horas na fila
    const longQueue = queueMin > QUEUE_SLA_BREACH_MINUTES;

    if (breachedW || severeQueue) {
      healthSum += 0;
    } else if (hasRiskTag(w) || score >= 80 || longQueue) {
      healthSum += hasRiskTag(w) ? 5 : 8;
    } else if (score >= 50) {
      healthSum += 28;
    } else if (queueHrs > 8) {
      healthSum += 45;
    } else {
      healthSum += 100;
    }
  }

  const slaCompliance = Math.max(0, Math.round(((total - breached) / total) * 100));
  const efficiencyRate = Math.max(0, Math.round(healthSum / total));

  return {
    slaCompliance,
    efficiencyRate,
    slaBreachedCount: breached,
    atRiskCount: atRisk,
    totalExecutions: total,
  };
}
